import { useEffect, useMemo, useRef, useState } from 'react';

import { SEARCH_DEBOUNCE_MS } from '../../../core/constants/forms';
import { IconDna, IconClose } from '../../../common/icons';
import { useCombobox } from '../../../common/hooks/use-combobox';
import { useSemenList } from '../contexts/semen';

import '../../clients/styles/search.css';

// Alto estimado del panel de resultados (max-height 240 + margen) para decidir si
// abrir hacia arriba cuando no hay espacio abajo (p. ej. última fila junto al footer).
const PANEL_SPACE = 260;

// Semen typeahead. Controlled by `value` (selected semen): value set → green
// locked field + clear X; value null → server search (q over code/name/sire).

export default function SemenSearch({ value, onSelect, onClear, autoFocus = false, placeholder = 'Buscar semen por código o nombre…' }) {
    const [text, setText] = useState('');
    const [debounced, setDebounced] = useState('');
    const [up, setUp] = useState(false); // abrir hacia arriba si no cabe abajo
    const inputRef = useRef(null);

    useEffect(() => {
        const t = setTimeout(() => setDebounced(text), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(t);
    }, [text]);

    const filters = useMemo(() => {
        const out = { limit: 15 };
        if (debounced.trim()) out.q = debounced.trim();
        return out;
    }, [debounced]);

    const { data, isFetching } = useSemenList(filters, { enabled: !value });
    const items = value ? [] : (data?.items ?? []);

    const choose = (s) => { onSelect?.(s); };
    const cb = useCombobox({ items, onChoose: choose });

    // Igual que el onFocus del hook, pero decidiendo antes si el panel abre hacia arriba.
    const openPanel = () => {
        const el = inputRef.current;
        if (el) setUp((window.innerHeight - el.getBoundingClientRect().bottom) < PANEL_SPACE);
        cb.setOpen(true);
    };

    if (value) {
        return (
            <div className="search">
                <div className="search__field search__field--selected">
                    <span className="search__field-icon"><IconDna /></span>
                    <span className="search__selected-text">
                        {value.code}{value.name ? ` · ${value.name}` : ''}
                    </span>
                    <button type="button" className="search__clear" onClick={() => { setText(''); onClear?.(); }} aria-label="Cambiar semen" title="Cambiar">
                        <IconClose />
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="search">
            <input
                ref={inputRef}
                type="text"
                className="search__input"
                value={text}
                placeholder={placeholder}
                autoFocus={autoFocus}
                role="combobox"
                aria-autocomplete="list"
                aria-expanded={cb.open}
                aria-controls={cb.listId}
                aria-activedescendant={cb.activeId}
                onChange={(e) => { setText(e.target.value); cb.setOpen(true); }}
                onFocus={openPanel}
                onBlur={cb.onBlur}
                onKeyDown={cb.onKeyDown}
            />
            {cb.open && (
                <div className={`search__panel ${up ? 'search__panel--up' : ''}`} role="listbox" id={cb.listId}>
                    {!items.length ? (
                        <p className="search__hint">{isFetching ? 'Buscando…' : 'Sin semen.'}</p>
                    ) : (
                        items.map((s, i) => (
                            <button
                                key={s.id}
                                type="button"
                                id={cb.optionId(i)}
                                role="option"
                                aria-selected={cb.activeIndex === i}
                                className={`search__item ${cb.activeIndex === i ? 'search__item--active' : ''}`}
                                onMouseDown={cb.onOptionMouseDown(s)}
                                onMouseEnter={() => cb.setActive(i)}
                            >
                                <span className="search__item-icon"><IconDna /></span>
                                <span className="search__item-body">
                                    <span className="search__item-name">{s.code}{s.name ? ` · ${s.name}` : ''}</span>
                                    <span className="search__item-sub">
                                        {[s.sire?.name || s.sire_name, s.origin === 'imported' ? 'Importado' : s.origin === 'national' ? 'Nacional' : null].filter(Boolean).join(' · ')}
                                    </span>
                                </span>
                            </button>
                        ))
                    )}
                </div>
            )}
        </div>
    );
}
